<template>
  <div>
    <div class="mb-4">
      <div class="mb-4">
        <label
          for="color-left"
          class="block mb-2 font-medium"
        >
          Colour 1: {{ hexToColorName(selectedColorLeft) }} ({{ selectedColorLeft }})
        </label>
        <ColorPicker
          v-model="selectedColorLeft"
          input-id="color-left"
          format="hex"
          inline
        />
      </div>
      <div>
        <label
          for="color-right"
          class="block mb-2 font-medium"
        >
          Colour 2: {{ hexToColorName(selectedColorRight) }} ({{ selectedColorRight }})
        </label>
        <ColorPicker
          v-model="selectedColorRight"
          input-id="color-right"
          format="hex"
          inline
        />
      </div>
    </div>
    <Button
      :label="isSubmitting ? 'Saving...' : 'Save'"
      class="mt-2 w-full"
      :loading="isSubmitting"
      :disabled="isSubmitting"
      severity="primary"
      icon="pi pi-check"
      @click="saveColour"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { projectStore } from '../projectStore';
import { useToastService } from '@/composables/useToastService';

const toast = useToastService();

const selectedColorLeft = ref('#ff0000');
const selectedColorRight = ref('#0000ff');
const isSubmitting = ref(false);

const colorNameToHex = (colorName) => {
    const colors = {
        red: '#ff0000',
        green: '#00ff00',
        blue: '#0000ff',
        orange: '#ffa500',
        yellow: '#ffff00',
        purple: '#800080',
        black: '#000000',
        grey: '#808080',
        gray: '#808080',
        brown: '#a52a2a'
    };
    return colors[colorName?.toLowerCase()] || '#808080';
};

const hexToColorName = (hex) => {
    hex = (hex || '').replace('#', '');
    if (hex.length !== 6) return 'grey';
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const colors = [
        { name: 'red', r: 255, g: 0, b: 0 },
        { name: 'green', r: 0, g: 255, b: 0 },
        { name: 'blue', r: 0, g: 0, b: 255 },
        { name: 'orange', r: 255, g: 165, b: 0 },
        { name: 'yellow', r: 255, g: 255, b: 0 },
        { name: 'purple', r: 128, g: 0, b: 128 },
        { name: 'black', r: 0, g: 0, b: 0 },
        { name: 'grey', r: 128, g: 128, b: 128 },
        { name: 'brown', r: 165, g: 42, b: 42 }
    ];
    let minDistance = Infinity;
    let closestColor = 'grey';
    colors.forEach((color) => {
        const distance = Math.sqrt(
            Math.pow(r - color.r, 2) + Math.pow(g - color.g, 2) + Math.pow(b - color.b, 2)
        );
        if (distance < minDistance) {
            minDistance = distance;
            closestColor = color.name;
        }
    });
    return closestColor;
};

const initFromProps = () => {
    const props = projectStore.currentModalProps || {};
    selectedColorLeft.value = colorNameToHex(props.currentColourLeft || 'red');
    selectedColorRight.value = colorNameToHex(props.currentColourRight || 'blue');
};

onMounted(initFromProps);
watch(() => projectStore.currentModalProps, initFromProps, { deep: true });

const saveColour = async () => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;

    try {
        const projectId = projectStore.currentProjectId;
        const equipmentListId = projectStore.currentEquipmentListId;
        const groupId = projectStore.currentDraggableItemId;
        const props = projectStore.currentModalProps || {};

        if (!projectId || !equipmentListId || !groupId) {
            throw new Error('Project ID, Equipment List ID or Group ID not found');
        }

        const { updateEquipmentListContent } = await import('@/services/projectService');
        const updateData = {
            name: props.groupName || '',
            icon_colour_left: hexToColorName(selectedColorLeft.value),
            icon_colour_right: hexToColorName(selectedColorRight.value)
        };

        await updateEquipmentListContent(projectId, equipmentListId, groupId, updateData);

        if (projectStore.refreshFunction && typeof projectStore.refreshFunction === 'function') {
            await projectStore.refreshFunction();
        }

        toast.add({
            severity: 'success',
            summary: 'Colour Updated',
            detail: 'Group colour has been updated',
            life: 3000
        });

        projectStore.closeModal();
    } catch (error) {
        console.error('Error updating group colour:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || error.message || 'Failed to update colour. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style scoped>
:deep(.p-colorpicker-panel) {
    box-shadow: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
}
</style>
